import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  Plus, 
  Trash2, 
  Save, 
  AlertTriangle, 
  CheckCircle,
  Clock,
  ArrowLeft,
  AlertOctagon,
  Lock
} from 'lucide-react';
import { Link } from 'react-router-dom';
import NotificationService from '../../services/notificationService';
import { useToast } from '../common/Toast';
import PageHeader from '../common/PageHeader';
import api from '../../services/api'; 

const ComplianceAlerts = () => {
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canEdit, setCanEdit] = useState(false); // Permissions state
  
  // Data State
  const [settings, setSettings] = useState({
    isActive: true, 
    escalationRules: []
  });
  
  // Positions fetched from User Database
  const [availablePositions, setAvailablePositions] = useState([
    'Contract Owner', 'Compliance Officer', 'IT Manager', 'Legal Counsel', 'Finance Director'
  ]);

  // Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);

  useEffect(() => {
    checkPermissions();
    loadSettings();
    fetchPositions();
  }, []);

  // --- 1. PERMISSION CHECK (Manager Only) ---
  const checkPermissions = () => {
    try {
      // Retrieve user info from local storage (standard for this app structure)
      const userStr = localStorage.getItem('userInfo') || localStorage.getItem('user');
      
      if (userStr) {
        const user = JSON.parse(userStr);
        const position = (user.position || '').toLowerCase();
        const role = (user.role?.name || user.role || '').toLowerCase();

        // Allow if position contains "manager" OR role is "admin"
        if (position.includes('manager') || role === 'admin') {
          setCanEdit(true);
        } else {
          setCanEdit(false);
        }
      } else {
        // Fallback: Default to false if no user info found (Security)
        setCanEdit(false);
      }
    } catch (e) {
      console.warn('Error checking permissions:', e);
      setCanEdit(false);
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await NotificationService.getComplianceSettings();
      if (response.data) {
        setSettings(response.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await api.get('/users');
      if (response.data && Array.isArray(response.data.data)) {
        // Extract unique positions
        const uniquePositions = [...new Set(
          response.data.data
            .map(user => user.position)
            .filter(pos => pos && typeof pos === 'string' && pos.trim().length > 0)
        )];
        
        if (uniquePositions.length > 0) {
          setAvailablePositions(uniquePositions.sort());
        }
      }
    } catch (err) {
      console.warn('Failed to fetch user positions, using defaults.', err);
    }
  };

  const handleSave = async () => {
    if (!canEdit) return; // Guard clause
    try {
      setSaving(true);
      const payload = { ...settings, isActive: true };
      await NotificationService.saveComplianceSettings(payload);
      success('Escalation rules saved successfully');
    } catch (err) {
      showError('Failed to save rules');
    } finally {
      setSaving(false);
    }
  };

  const addRule = (type = 'before_due') => {
    if (!canEdit) return;
    const isEscalation = type === 'after_due';
    setSettings(prev => ({
      ...prev,
      escalationRules: [
        ...prev.escalationRules,
        { 
          triggerType: type, 
          daysOffset: isEscalation ? 1 : 7, 
          action: isEscalation ? 'escalation' : 'notification', 
          recipientRoles: isEscalation ? ['Compliance Officer'] : ['Contract Owner'] 
        }
      ]
    }));
  };

  const confirmDelete = (index) => {
    if (!canEdit) return;
    setRuleToDelete(index);
    setDeleteModalOpen(true);
  };

  const executeDelete = () => {
    if (ruleToDelete === null) return;
    
    const newRules = [...settings.escalationRules];
    newRules.splice(ruleToDelete, 1);
    setSettings(prev => ({ ...prev, escalationRules: newRules }));
    
    setDeleteModalOpen(false);
    setRuleToDelete(null);
    success('Rule removed');
  };

  const updateRule = (index, field, value) => {
    if (!canEdit) return;
    const newRules = [...settings.escalationRules];
    
    // Handle Roles array conversion
    if (field === 'recipientRoles') {
      newRules[index] = { ...newRules[index], [field]: [value] };
    } else {
      newRules[index] = { ...newRules[index], [field]: value };
    }
    
    setSettings(prev => ({ ...prev, escalationRules: newRules }));
  };

  const hasEscalationRule = settings.escalationRules.some(r => r.triggerType === 'after_due');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto relative">
      
      {/* Navigation */}
      <div className="mb-4">
        <Link 
          to="/notifications/center" 
          className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Notification Center
        </Link>
      </div>

      {/* --- 2. HEADER WITH SHADOW (Awkward White Header Fix) --- */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <PageHeader 
          title="Compliance Escalation Rules" 
          description="Configure automated triggers, reminders, and escalations for contract obligations."
        />
        {!canEdit && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-center text-yellow-800 text-sm">
            <Lock size={16} className="mr-2" />
            <span>Read-Only Mode: Only Managers can modify these settings.</span>
          </div>
        )}
      </div>

      {/* Main Control Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex justify-end items-center mb-6">
          {canEdit && (
            <button 
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition-colors"
            >
              <Save size={16} />
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          )}
        </div>

        {/* Rules Table */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Trigger Event</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Timing (Days)</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Recipient Position</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Alert Type</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {settings.escalationRules.length > 0 ? (
                settings.escalationRules.map((rule, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    {/* Trigger Selector */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select 
                        disabled={!canEdit}
                        className={`block w-full border-gray-300 rounded-md shadow-sm text-sm py-1.5 ${!canEdit ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'focus:ring-blue-500 focus:border-blue-500'}`}
                        value={rule.triggerType}
                        onChange={(e) => updateRule(index, 'triggerType', e.target.value)}
                      >
                        <option value="before_due">Before Due Date</option>
                        <option value="after_due">After Due Date (Overdue)</option>
                      </select>
                    </td>

                    {/* Timing Input */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <input 
                          disabled={!canEdit}
                          type="number" 
                          min="0"
                          className={`w-20 border-gray-300 rounded-md shadow-sm text-sm py-1.5 ${!canEdit ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                          value={rule.daysOffset}
                          onChange={(e) => updateRule(index, 'daysOffset', parseInt(e.target.value) || 0)}
                        />
                        <span className="text-sm text-gray-500">days</span>
                      </div>
                    </td>

                    {/* Position Dropdown */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select 
                        disabled={!canEdit}
                        className={`block w-full border-gray-300 rounded-md shadow-sm text-sm py-1.5 ${!canEdit ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'focus:ring-blue-500 focus:border-blue-500'}`}
                        value={Array.isArray(rule.recipientRoles) ? rule.recipientRoles[0] : rule.recipientRoles}
                        onChange={(e) => updateRule(index, 'recipientRoles', e.target.value)}
                      >
                        <option value="" disabled>Select Position...</option>
                        {availablePositions.map((pos, pIndex) => (
                          <option key={pIndex} value={pos}>{pos}</option>
                        ))}
                      </select>
                    </td>

                    {/* Type Badge */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        rule.triggerType === 'after_due' 
                          ? 'bg-red-100 text-red-800 border border-red-200' 
                          : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                      }`}>
                        {rule.triggerType === 'after_due' ? 'Escalation' : 'Reminder'}
                      </span>
                    </td>

                    {/* Delete Action */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {canEdit && (
                        <button 
                          onClick={() => confirmDelete(index)}
                          className="text-gray-400 hover:text-red-600 transition-colors p-1"
                          title="Remove Rule"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <Bell size={48} className="text-gray-200 mb-3" />
                      <p className="text-sm">No escalation rules configured.</p>
                      {canEdit && (
                        <p className="text-xs text-gray-400 mt-1">Click "Add New Rule" to create your first alert.</p>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Add Buttons (Only if Manager) */}
        {canEdit && (
          <div className="mt-4 flex gap-3">
            <button 
              onClick={() => addRule('before_due')}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
            >
              <Plus size={18} /> Add New Reminder
            </button>

            {!hasEscalationRule && (
              <button 
                onClick={() => addRule('after_due')}
                className="flex items-center gap-2 text-red-600 hover:text-red-800 font-medium text-sm transition-colors"
              >
                <AlertTriangle size={18} /> Add Escalation Rule
              </button>
            )}
          </div>
        )}
      </div>

      {/* Helper Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="p-5 bg-blue-50 rounded-xl border border-blue-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2 text-blue-800 font-semibold">
            <Clock size={18} /> 
            <h3>Reminders</h3>
          </div>
          <p className="text-sm text-blue-600 leading-relaxed">
            Configure "Before Due Date" triggers to warn owners 30, 14, or 7 days in advance.
          </p>
        </div>

        <div className="p-5 bg-red-50 rounded-xl border border-red-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2 text-red-800 font-semibold">
            <AlertTriangle size={18} /> 
            <h3>Escalations</h3>
          </div>
          <p className="text-sm text-red-600 leading-relaxed">
            Set "After Due Date" triggers to notify Compliance Officers immediately when overdue.
          </p>
        </div>

        <div className="p-5 bg-green-50 rounded-xl border border-green-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2 text-green-800 font-semibold">
            <CheckCircle size={18} /> 
            <h3>Automation</h3>
          </div>
          <p className="text-sm text-green-600 leading-relaxed">
            The notifications service evaluates obligations daily and sends alerts based on your configuration.
          </p>
        </div>
      </div>

      {/* --- Delete Confirmation Modal --- */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6 transform transition-all scale-100">
            <div className="flex items-center gap-3 mb-4 text-red-600">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertOctagon size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Delete Rule?</h3>
            </div>
            
            <p className="text-gray-600 mb-6 text-sm">
              Are you sure you want to remove this alert rule? Users will no longer receive notifications for this trigger.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={executeDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete Rule
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ComplianceAlerts;